import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CountdownToLaunch } from '../components/CountdownToLaunch';
import {
    Coins, Users, Lock, Sparkles, Flame, Wallet, ArrowRight, PieChart,
    Building2, TrendingUp,
} from 'lucide-react';

const TOTAL_SUPPLY = 1_000_000_000;
const TGE_PRICE = 0.04; // $0.04 per B4P

const ALLOCATIONS = [
    { key: 'community', pct: 40, amount: 400_000_000, color: '#00FF66', icon: Users, descKey: 'tokenomics.allocCommunityDesc' },
    { key: 'sale', pct: 25, amount: 250_000_000, color: '#FFD700', icon: Coins, descKey: 'tokenomics.allocSaleDesc' },
    { key: 'team', pct: 15, amount: 150_000_000, color: '#9B6FE8', icon: Users, descKey: 'tokenomics.allocTeamDesc' },
    { key: 'liquidity', pct: 10, amount: 100_000_000, color: '#0066CC', icon: Lock, descKey: 'tokenomics.allocLiquidityDesc' },
    { key: 'treasury', pct: 10, amount: 100_000_000, color: '#FF6B35', icon: Building2, descKey: 'tokenomics.allocTreasuryDesc' },
];

// SVG donut chart
function DonutChart({ data, size = 280 }) {
    const radius = size / 2 - 24;
    const cx = size / 2;
    const cy = size / 2;
    const circumference = 2 * Math.PI * radius;
    let offset = 0;
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
            <circle cx={cx} cy={cy} r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="36" />
            {data.map((d) => {
                const dash = (d.pct / 100) * circumference;
                const seg = (
                    <circle
                        key={d.key}
                        cx={cx}
                        cy={cy}
                        r={radius}
                        fill="none"
                        stroke={d.color}
                        strokeWidth="36"
                        strokeDasharray={`${dash} ${circumference - dash}`}
                        strokeDashoffset={-offset}
                    />
                );
                offset += dash;
                return seg;
            })}
        </svg>
    );
}

export default function TokenomicsPage() {
    const { t } = useTranslation();

    return (
        <div className="min-h-screen" data-testid="tokenomics-page">
            {/* Hero */}
            <section className="relative overflow-hidden border-b border-white/10">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/10 via-transparent to-[#0066CC]/10" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-20 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/30 mb-6">
                        <PieChart className="w-3.5 h-3.5 text-[#FFD700]" />
                        <span className="text-[#FFD700] text-xs font-bold uppercase tracking-widest">
                            {t('tokenomics.heroBadge')}
                        </span>
                    </div>
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-4">
                        B4P <span className="text-[#FFD700]">{t('tokenomics.heroTitle')}</span>
                    </h1>
                    <p className="text-lg text-white/70 max-w-2xl mx-auto mb-8">
                        {t('tokenomics.heroDesc')}
                    </p>
                    <CountdownToLaunch />
                </div>
            </section>

            {/* Key Numbers */}
            <section className="py-16 border-b border-white/10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Card className="bg-white/[0.04] border-white/10 text-center" data-testid="metric-supply">
                            <CardContent className="p-5">
                                <Coins className="w-6 h-6 text-[#FFD700] mx-auto mb-2" />
                                <div className="text-2xl sm:text-3xl font-black text-white">1B</div>
                                <div className="text-xs text-white/50 uppercase tracking-wider mt-1">{t('tokenomics.totalSupply')}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/[0.04] border-white/10 text-center" data-testid="metric-tge">
                            <CardContent className="p-5">
                                <Sparkles className="w-6 h-6 text-[#00FF66] mx-auto mb-2" />
                                <div className="text-2xl sm:text-3xl font-black text-white">${TGE_PRICE}</div>
                                <div className="text-xs text-white/50 uppercase tracking-wider mt-1">{t('tokenomics.tgePrice')}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/[0.04] border-white/10 text-center" data-testid="metric-fdv">
                            <CardContent className="p-5">
                                <TrendingUp className="w-6 h-6 text-[#9B6FE8] mx-auto mb-2" />
                                <div className="text-2xl sm:text-3xl font-black text-white">$40M</div>
                                <div className="text-xs text-white/50 uppercase tracking-wider mt-1">{t('tokenomics.fdv')}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/[0.04] border-white/10 text-center" data-testid="metric-chain">
                            <CardContent className="p-5">
                                <Wallet className="w-6 h-6 text-[#0066CC] mx-auto mb-2" />
                                <div className="text-2xl sm:text-3xl font-black text-white">Base</div>
                                <div className="text-xs text-white/50 uppercase tracking-wider mt-1">{t('tokenomics.chain')}</div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Distribution */}
            <section className="py-20 border-b border-white/10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <h2 className="text-3xl sm:text-4xl font-black text-white text-center mb-3">
                        {t('tokenomics.distTitle')}
                    </h2>
                    <p className="text-white/60 text-center max-w-2xl mx-auto mb-12">
                        {t('tokenomics.distDesc')}
                    </p>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="flex justify-center">
                            <DonutChart data={ALLOCATIONS} />
                        </div>
                        <div className="space-y-3">
                            {ALLOCATIONS.map((a) => {
                                const Icon = a.icon;
                                return (
                                    <div
                                        key={a.key}
                                        className="flex items-start gap-4 p-4 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/15 transition-colors"
                                        data-testid={`alloc-${a.key}`}
                                    >
                                        <div
                                            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                                            style={{ backgroundColor: `${a.color}22` }}
                                        >
                                            <Icon className="w-5 h-5" style={{ color: a.color }} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="font-bold text-white">{t(`tokenomics.alloc_${a.key}`)}</span>
                                                <span className="font-mono font-black" style={{ color: a.color }}>{a.pct}%</span>
                                            </div>
                                            <div className="text-xs text-white/50 mb-1">
                                                {(a.amount / 1_000_000).toFixed(0)}M B4P
                                            </div>
                                            <p className="text-sm text-white/70 leading-relaxed">{t(a.descKey)}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* Vesting */}
            <section className="py-20 border-b border-white/10 bg-gradient-to-br from-[#001A4D]/30 to-transparent">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <h2 className="text-3xl sm:text-4xl font-black text-white text-center mb-3">
                        {t('tokenomics.vestingTitle')}
                    </h2>
                    <p className="text-white/60 text-center mb-12">
                        {t('tokenomics.vestingDesc')}
                    </p>
                    <div className="space-y-4">
                        <Card className="bg-white/[0.04] border-white/10">
                            <CardContent className="p-5 flex items-center gap-4">
                                <Coins className="w-8 h-8 text-[#00FF66] shrink-0" />
                                <div className="flex-1">
                                    <div className="font-bold text-white">{t('tokenomics.vestSale')}</div>
                                    <div className="text-sm text-white/60">{t('tokenomics.vestSaleSchedule')}</div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/[0.04] border-white/10">
                            <CardContent className="p-5 flex items-center gap-4">
                                <Users className="w-8 h-8 text-[#9B6FE8] shrink-0" />
                                <div className="flex-1">
                                    <div className="font-bold text-white">{t('tokenomics.vestTeam')}</div>
                                    <div className="text-sm text-white/60">{t('tokenomics.vestTeamSchedule')}</div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/[0.04] border-white/10">
                            <CardContent className="p-5 flex items-center gap-4">
                                <Lock className="w-8 h-8 text-[#0066CC] shrink-0" />
                                <div className="flex-1">
                                    <div className="font-bold text-white">{t('tokenomics.vestLiquidity')}</div>
                                    <div className="text-sm text-white/60">{t('tokenomics.vestLiquiditySchedule')}</div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-[#FF6B35]/5 border-[#FF6B35]/20">
                            <CardContent className="p-5 flex items-center gap-4">
                                <Flame className="w-8 h-8 text-[#FF6B35] shrink-0" />
                                <div className="flex-1">
                                    <div className="font-bold text-white">{t('tokenomics.deflationary')}</div>
                                    <div className="text-sm text-white/60">{t('tokenomics.deflationaryDesc')}</div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
                    <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
                        {t('tokenomics.ctaTitle')}
                    </h2>
                    <p className="text-white/60 mb-8">{t('tokenomics.ctaDesc')}</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/b4p-launch">
                            <Button size="lg" className="bg-[#FFD700] text-[#001A4D] hover:bg-[#FFC700] font-bold px-8 py-6" data-testid="tokenomics-cta-buy">
                                <Wallet className="w-5 h-5 mr-2" />
                                {t('tokenomics.ctaBuy')}
                            </Button>
                        </Link>
                        <Link to="/roadmap">
                            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/5 px-8 py-6" data-testid="tokenomics-cta-roadmap">
                                {t('tokenomics.ctaRoadmap')}
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
